import { FullScreenLayout } from '@layouts/FullScreenLayout';
import { lazy } from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { Flags, useFlag } from 'ui';
import { ProtectedRoute } from '@components/ProtectedRoute';
import { FullScreenLayoutProvider } from '@context/FullScreenLayoutContext';
import { CaseDashboardProvider } from '@pages/CaseDashboard/CaseDashboardContext';
import { NotFound } from '@pages/NotFound';
import { ComingSoonProduct } from '@pages/ProductComingSoon/types';
import { UploadClaimsFileProvider } from '@pages/UploadClaimsFile/UploadClaimsFileContext';
import { UsersManagementProvider } from '@pages/UsersManagement/UsersManagementContext';
import { PATHS } from '@utils/paths';
import { PrivateLayout } from '../../../layouts/PrivateLayout';
import { LEGACY_REDIRECTS } from './constants';
import { LegacyRedirect } from './LegacyRedirect';

const Login = lazy(() => import('../../../pages/Login'));
const ForgotPassword = lazy(() => import('../../../pages/ForgotPassword'));
const CaseDashboard = lazy(() => import('../../../pages/CaseDashboard'));
const ClaimForm = lazy(() => import('../../../pages/ClaimForm'));
const UsersManagement = lazy(() => import('../../../pages/UsersManagement'));
const Security = lazy(() => import('../../../pages/Security'));
const AccountActivation = lazy(
  () => import('../../../pages/AccountActivation'),
);
const AuthManagement = lazy(() => import('../../../pages/AuthManagement'));
const ResetPassword = lazy(() => import('../../../pages/ResetPassword'));
const ScheduledReports = lazy(() => import('../../../pages/ScheduledReports'));
const CaseSettings = lazy(() => import('../../../pages/CaseSettings'));
const NewCaseCreation = lazy(() => import('../../../pages/NewCaseCreation'));
const UploadClaimsFile = lazy(() => import('../../../pages/UploadClaimsFile'));
const ClaimFiles = lazy(() => import('../../../pages/ClaimFiles'));
const InvalidLinkInvitation = lazy(
  () => import('../../../pages/InvalidLinkInvitation'),
);

const DeveloperTools = lazy(() => import('../../../pages/DeveloperTools'));
const ClaimsExport = lazy(() => import('../../../pages/ClaimsExport'));
const ClaimscoreSettings = lazy(
  () => import('../../../pages/ClaimscoreSettings'),
);
const ProductComingSoon = lazy(
  () => import('../../../pages/ProductComingSoon'),
);

export const useAppRoutes = () => {
  const isUploadClaimsFileEnabled = useFlag(Flags.uploadClaimsFile);

  const publicRoutes: RouteObject[] = [
    { path: PATHS.PUBLIC.login, element: <Login /> },
    { path: PATHS.PUBLIC.forgotPassword, element: <ForgotPassword /> },
    { path: PATHS.PUBLIC.auth, element: <AuthManagement /> },
    { path: PATHS.PUBLIC.passwordReset, element: <ResetPassword /> },
    {
      path: PATHS.PUBLIC.accountActivation,
      element: <AccountActivation />,
    },
    {
      path: PATHS.PUBLIC.pageNotFound,
      element: <NotFound />,
    },
    {
      path: PATHS.PUBLIC.invalidLinkInvitation,
      element: <InvalidLinkInvitation />,
    },
    {
      path: '*',
      element: <Navigate to="/404" />,
    },
  ];

  const privateClaimFormRoute: RouteObject[] = [
    {
      path: PATHS.PRIVATE_BASE.root,
      element: <PrivateLayout />,
      children: [
        { path: '/claim-form', element: <ClaimForm /> },
        { path: PATHS.PRIVATE_BASE.claimsExport, element: <ClaimsExport /> },
      ],
    },
  ];

  const noLayoutPrivateRoutes: RouteObject[] = [
    {
      path: PATHS.PRIVATE_BASE.newCaseCreation,
      element: (
        <FullScreenLayoutProvider>
          <FullScreenLayout>
            <NewCaseCreation />
          </FullScreenLayout>
        </FullScreenLayoutProvider>
      ),
    },
    {
      path: PATHS.PRIVATE_BASE.uploadClaimsFile,
      element: (
        <ProtectedRoute hasAccess={Boolean(isUploadClaimsFileEnabled)}>
          <FullScreenLayoutProvider>
            <FullScreenLayout>
              <UploadClaimsFileProvider>
                <UploadClaimsFile />
              </UploadClaimsFileProvider>
            </FullScreenLayout>
          </FullScreenLayoutProvider>
        </ProtectedRoute>
      ),
    },
    {
      path: PATHS.PRIVATE_BASE.security,
      element: (
        <FullScreenLayoutProvider>
          <FullScreenLayout>
            <Security />
          </FullScreenLayout>
        </FullScreenLayoutProvider>
      ),
    },
  ];

  const privateCaseLayoutRoutes: RouteObject[] = [
    {
      path: PATHS.PRIVATE_BASE.root,
      element: <PrivateLayout withNavbar withSidebar />,
      children: [
        {
          path: '',
          element: <Navigate to={PATHS.PRIVATE_BASE.dashboard} />,
        },
        {
          index: true,
          path: PATHS.PRIVATE_BASE.dashboard,
          element: (
            <CaseDashboardProvider>
              <CaseDashboard />
            </CaseDashboardProvider>
          ),
        },
        {
          path: PATHS.PRIVATE_BASE.claimFiles,
          element: (
            <ProtectedRoute hasAccess={Boolean(isUploadClaimsFileEnabled)}>
              <ClaimFiles />
            </ProtectedRoute>
          ),
        },
        {
          path: PATHS.PRIVATE_BASE.permissions,
          element: (
            <UsersManagementProvider>
              <UsersManagement />
            </UsersManagementProvider>
          ),
        },
        {
          path: PATHS.PRIVATE_BASE.scheduledReports,
          element: <ScheduledReports />,
        },
        {
          path: PATHS.PRIVATE_BASE.claimscoreSettings,
          element: <ClaimscoreSettings />,
        },
        {
          path: PATHS.PRIVATE_BASE.caseSetup,
          element: <CaseSettings />,
        },
        {
          path: PATHS.PRIVATE_BASE.developerTools,
          element: <DeveloperTools />,
        },
        {
          path: PATHS.PRIVATE_BASE.classResolution,
          element: (
            <ProductComingSoon product={ComingSoonProduct.ClassResolution} />
          ),
        },
        {
          path: PATHS.PRIVATE_BASE.deepValidation,
          element: (
            <ProductComingSoon product={ComingSoonProduct.DeepValidation} />
          ),
        },
        ...LEGACY_REDIRECTS.map(({ from, to }) => ({
          path: from,
          element: <LegacyRedirect to={to} />,
        })),
      ],
    },
  ];

  const routes: RouteObject[] = [
    ...publicRoutes,
    ...privateCaseLayoutRoutes,
    ...privateClaimFormRoute,
    ...noLayoutPrivateRoutes,
  ];

  const router = createBrowserRouter(routes);

  return { routes, router };
};

export default useAppRoutes;
